// HelpPill — small "?" icon button that opens an inline popover with
// title + body copy looked up from HELP[topic] in mec_help.js.
//
// Usage:
//   <HelpPill topic="health_score" />
//
// Accessibility:
//   • aria-label includes the topic title.
//   • ESC closes when open and focus is anywhere in the document.
//   • Click outside the popover closes it.
//   • Keyboard activation: Enter / Space toggle via the native button.
//
// The popover renders absolutely inside the same element — no React portal
// to keep mounting simple. Parent must allow overflow visible.

import { useEffect, useRef, useState } from 'react';
import { HelpCircle, X } from 'lucide-react';
import { HELP } from '../mec_help';

const SIZE_CLASS = {
  sm: 'w-4 h-4',
  md: 'w-[18px] h-[18px]',
};

export default function HelpPill({ topic, size = 'sm' }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const entry = HELP[topic];
  const title = entry?.title || 'Help';
  const body = entry?.body || '';

  // Close on outside click + ESC. Listeners only when open.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!entry) return null;

  const iconSize = SIZE_CLASS[size] || SIZE_CLASS.sm;

  return (
    <span ref={wrapperRef} className="relative inline-flex align-middle">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label={`Help: ${title}`}
        aria-expanded={open}
        tabIndex={0}
        className={`
          inline-flex items-center justify-center
          text-[var(--color-warm-400)] hover:text-[var(--color-warm-700)]
          focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-warm-300)]
          rounded-full transition-colors
        `}
      >
        <HelpCircle className={iconSize} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={title}
          className={`
            absolute top-full left-1/2 -translate-x-1/2 mt-2 z-30
            w-[280px] max-w-[80vw]
            bg-[var(--color-cream-50)]
            border border-[var(--color-warm-200)]
            rounded-[var(--radius-md)]
            shadow-[var(--shadow-md)]
            p-3.5
          `}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start gap-2 mb-1.5">
            <div className="text-[12px] font-bold uppercase tracking-[0.8px] text-[var(--color-warm-500)] flex-1">
              {title}
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close help"
              className="shrink-0 text-[var(--color-warm-400)] hover:text-[var(--color-warm-700)]"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="text-[12.5px] leading-[1.5] text-[var(--color-warm-700)]">
            {body}
          </div>
        </div>
      )}
    </span>
  );
}
